import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n, LANGS } from "../lib/i18n";
import { api, useFetch } from "../lib/api";
import { Logo } from "./Common";

const Newsletter = () => {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return setErr(t("invalid_email"));
    if (!consent) return setErr(t("required"));
    try { await api.post("/newsletter", { email, consent, lang }); setDone(true); toast.success(t("subscribed")); } catch { setErr(t("error")); }
  };
  return (
    <section className="bg-[color:var(--hw-beige)]" data-testid="newsletter-section">
      <div className="container-hw py-14 grid lg:grid-cols-2 gap-8 items-center">
        <div><h2 className="h2 mb-2">{t("newsletter")}</h2><p className="text-sm text-[color:var(--hw-muted)] max-w-md">{t("newsletter_d")}</p></div>
        {done ? <p className="font-semibold" data-testid="newsletter-success">{t("subscribed")}</p> : (
          <form onSubmit={submit} noValidate className="space-y-3">
            <div className="flex gap-2">
              <input className={`field ${err ? "field-err" : ""}`} type="email" placeholder={t("email")} aria-label={t("email")} value={email} onChange={(e) => { setEmail(e.target.value); setErr(""); }} data-testid="newsletter-email-input" />
              <button className="btn-dark" data-testid="newsletter-submit-button">{t("subscribe")}</button>
            </div>
            <label className="flex gap-2 text-xs text-[color:var(--hw-muted)] items-start"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="newsletter-consent-checkbox" className="mt-0.5" />{t("newsletter_consent")} <Link to="/privacy" className="link-u">{t("privacy")}</Link></label>
            {err && <p className="text-xs text-[color:var(--hw-sale)]" role="alert" data-testid="newsletter-error">{err}</p>}
          </form>
        )}
      </div>
    </section>
  );
};

export const CookieBanner = () => {
  const { t } = useI18n();
  const [choice, setChoice] = useState(() => localStorage.getItem("hw_consent"));
  if (choice) return null;
  const set = (v) => { localStorage.setItem("hw_consent", v); setChoice(v); };
  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-md z-50 bg-white border border-[color:var(--hw-line)] shadow-xl p-5" role="dialog" aria-label={t("cookies")} data-testid="cookie-banner">
      <p className="text-sm mb-4">{t("cookie_banner")} <Link to="/cookies" className="link-u">{t("cookies")}</Link></p>
      <div className="flex gap-2"><button className="btn-primary h-9" onClick={() => set("all")} data-testid="cookie-accept-button">{t("accept")}</button><button className="btn-outline h-9" onClick={() => set("necessary")} data-testid="cookie-decline-button">{t("decline")}</button></div>
    </div>
  );
};

export default function Footer({ settings }) {
  const { t, lang, setLang, currency } = useI18n();
  const col = (title, links) => (
    <div><p className="eyebrow !text-white/60 mb-4">{title}</p><ul className="space-y-2.5 text-sm">{links.map(([to, l]) => <li key={to}><Link to={to} className="text-white/85 hover:text-white hover:underline">{l}</Link></li>)}</ul></div>
  );
  const c = settings?.contact || {};
  const { data: cats } = useFetch(`/categories?lang=${lang}`, { cacheable: true });
  return (
    <footer>
      <Newsletter />
      <div className="bg-[color:var(--hw-ink)] text-white" data-testid="site-footer">
        <div className="container-hw py-14 grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-1 space-y-4">
            <Logo light size="h-20" />
            <address className="not-italic text-sm text-white/70 leading-relaxed">{c.company}<br />{c.address}<br />{c.email}<br />{c.phone}<br />UID: {c.uid}</address>
            <div className="flex gap-2 pt-2">{LANGS.map((l) => <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 text-xs uppercase border border-white/30 ${l === lang ? "bg-white text-black" : ""}`} data-testid={`footer-lang-${l}`}>{l}</button>)}</div>
          </div>
          {col(t("shop"), [...["beds", "mattresses", "wardrobes", "sofas", "dining-tables", "desks"].map((s) => [`/c/${s}`, (cats || []).find((c) => c.slug === s)?.name || s]), ["/products", t("all_products")]])}
          {col(t("service"), [["/contact", t("contact")], ["/faq", t("faq")], ["/track-order", t("track_order")], ["/shipping", t("shipping_page")], ["/returns", t("returns_page")], ["/assembly", t("assembly_page")], ["/care", t("care_page")], ["/warranty", t("warranty_page")]])}
          {col(t("policies"), [["/about", t("about")], ["/privacy", t("privacy")], ["/terms", t("terms")], ["/cookies", t("cookies")]])}
          <div><p className="eyebrow !text-white/60 mb-4">Social</p><ul className="space-y-2.5 text-sm text-white/85">{["instagram", "pinterest", "facebook"].map((s) => <li key={s}>{settings?.social?.[s] ? <a href={settings.social[s]} rel="noopener noreferrer" target="_blank" className="hover:underline capitalize">{s}</a> : <span className="capitalize text-white/40">{s}</span>}</li>)}</ul></div>
        </div>
        <div className="border-t border-white/10"><div className="container-hw py-5 text-xs text-white/50 flex flex-wrap gap-4 justify-between"><span>© {new Date().getFullYear()} ANEL Furniture · {currency}</span><span>Visa · Mastercard · Stripe</span></div></div>
      </div>
    </footer>
  );
}
